/**
 * CommunitySuccessModal Component
 *
 * Displays a success modal after community creation.
 * Shows confirmation message with action buttons and social share options.
 */

import { useState } from "react"
import { toast } from "sonner"
import {
  FacebookIcon,
  InstagramIcon,
  LinkShareIcon,
  ThreadsIcon,
  TikTokIcon,
} from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader } from "@/components/ui/dialog"
import { ConnectInstagramPrompt } from "@/features/social-share/components/ConnectInstagramPrompt"
import { InstagramShareDialog } from "@/features/social-share/components/InstagramShareDialog"
import { useInstagramConnection } from "@/features/social-share/hooks/useInstagramConnection"
import { useTranslation } from "@/language"

interface CommunitySuccessModalProps {
  isOpen: boolean
  communityId: string
  communityName?: string
  communityImageUrl?: string | null
  overlayClass?: string
  onViewCommunity: () => void
}

export function CommunitySuccessModal({
  isOpen,
  communityId,
  communityName = "",
  communityImageUrl = null,
  overlayClass,
  onViewCommunity,
}: CommunitySuccessModalProps) {
  const { t } = useTranslation("communities")
  const [showPublishDialog, setShowPublishDialog] = useState(false)
  const { isConnected: isInstagramConnected } = useInstagramConnection()

  const communityUrl = `${window.location.origin}/communities/${communityId}`

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(communityUrl)
      toast.success(t("createCommunity.linkCopied"))
    } catch {
      toast.error(t("createCommunity.linkCopyFailed"))
    }
  }

  const handleInviteFriends = () => {
    onViewCommunity()
  }

  const handleInstagramClick = () => {
    if (isInstagramConnected) {
      setShowPublishDialog(true)
    }
  }

  const handleFacebookShare = () => {
    const shareText = communityName
      ? `Check out this community on Rituality: ${communityName}`
      : "Check out this community on Rituality!"
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(communityUrl)}&quote=${encodeURIComponent(shareText)}`,
      "_blank",
      "width=600,height=400",
    )
  }

  // When the publish dialog is open, render it on top of the success modal
  if (showPublishDialog) {
    return (
      <>
        <Dialog open={isOpen}>
          <DialogContent
            showCloseButton={false}
            className="sm:max-w-[600px] text-center p-8"
            overlayClass={overlayClass}
          >
            <DialogHeader className="flex flex-col items-center">
              <h2 className="text-[24px] font-bold text-white mt-6">
                {t("createCommunity.communityIsLive")}
              </h2>
              <DialogDescription className="text-primary mt-3 text-center">
                <p>{t("createCommunity.exploreDescription")}</p>
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
        <InstagramShareDialog
          open={showPublishDialog}
          onOpenChange={setShowPublishDialog}
          shareContext={{
            type: "community",
            communityId,
            name: communityName || "Community",
            imageUrl: communityImageUrl,
          }}
        />
      </>
    )
  }

  return (
    <Dialog open={isOpen}>
      <DialogContent
        showCloseButton={false}
        className="sm:max-w-[600px] text-center p-8"
        overlayClass={overlayClass}
      >
        <DialogHeader className="flex flex-col items-center">
          {/* Title */}
          <h2 className="text-[24px] font-bold text-white mt-6">
            {t("createCommunity.communityIsLive")}
          </h2>

          {/* Description */}
          <DialogDescription className="text-primary mt-3 text-center">
            <p>{t("createCommunity.exploreDescription")}</p>
          </DialogDescription>
        </DialogHeader>

        {/* Action Buttons */}
        <div className="flex flex-col gap-3 mt-1 items-center justify-center w-full">
          {/* Primary Button - View */}
          <Button onClick={onViewCommunity} className="w-[280px] bg-white font-semibold uppercase">
            {t("createCommunity.view")}
          </Button>

          {/* Secondary Button - Invite Friends */}
          <Button
            onClick={handleInviteFriends}
            variant="outline"
            className="w-full w-[280px] font-semibold uppercase"
          >
            {t("createCommunity.inviteFriends")}
          </Button>
        </div>

        {/* Share to Social Media Section */}
        <div className="mt-3 mb-7">
          <p className="text-white text-sm mb-3">{t("createCommunity.shareToSocial")}</p>

          {/* Social Media Icons */}
          <div className="flex items-center justify-center gap-2.5 mt-1">
            {/* Instagram — now opens publish dialog when connected */}
            <button
              type="button"
              onClick={handleInstagramClick}
              className="w-9 h-9 rounded-full bg-white/6 border border-white/30 flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Instagram"
            >
              <InstagramIcon width={20} height={20} className="text-white" strokeWidth={2} />
            </button>

            {/* Facebook */}
            <button
              type="button"
              onClick={handleFacebookShare}
              className="w-9 h-9 rounded-full bg-white/6 border border-white/30  flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Facebook"
            >
              <FacebookIcon width={18} height={18} className="text-off-white" />
            </button>

            {/* TikTok */}
            <button
              type="button"
              className="w-9 h-9 rounded-full bg-white/6 border border-white/30  flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="TikTok"
            >
              <TikTokIcon width={14} height={16} className="text-white" />
            </button>

            {/* Threads */}
            <button
              type="button"
              className="w-9 h-9 rounded-full bg-white/6 border border-white/30  flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Threads"
            >
              <ThreadsIcon width={15} height={18} className="text-off-white" />
            </button>

            {/* Link Button */}
            <button
              type="button"
              onClick={handleCopyLink}
              className="w-9 h-9 rounded-full bg-white/6 border border-white/30  flex items-center justify-center hover:bg-white/30 transition-colors"
              aria-label="Copy link"
            >
              <LinkShareIcon width={15} height={8} className="text-off-white" />
            </button>
          </div>

          {/* Connect prompt when unconnected user clicks Instagram */}
          {!isInstagramConnected && (
            <div className="mt-3 flex justify-center">
              <ConnectInstagramPrompt />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
